// Pure helpers of the page of one of our ads (web/ui/studio-pub.js): which videos to show, the
// latest version of an image or a video, why an element has no file yet, the progress of the
// making, what blocks it, where the script of an ad on brief stands and what the owner may do
// with it, and the fields of a description. DOM-free, so node:test covers them.
/** History notes that say nothing about why an element waits (queue locks, attempts started). */
const NOISE_RE = /^(Pris pour \d+ min|Essai \d+\s*:|→ )/;
/** Tag of a scene whose making a person forced (server/studio/templates.js FORCED_TAG). */
export const FORCED_TAG = 'forcee';
/** Fields never shown in a description (internal). */
const HIDDEN_FIELDS = new Set(['render']);
/** A description field above this length is shown as a long text. */
const LONG_CHARS = 140;

const byAdded = (a, b) => String(a.addedAt || '').localeCompare(String(b.addedAt || ''));

/**
 * The files of an element of one media type, oldest first.
 * @param {{files?: object[]}} item
 * @param {'image'|'video'} media
 * @returns {object[]}
 */
export function mediaFiles(item, media) {
  return (item?.files || []).filter((f) => f.media === media).sort(byAdded);
}

/**
 * The most recent file of a media type with the role « final » (the result), else null.
 * @param {{files?: object[]}} item
 * @param {'image'|'video'} media
 * @returns {object|null}
 */
export function latestFinal(item, media) {
  const list = mediaFiles(item, media).filter((f) => f.role === 'final');
  return list.length ? list[list.length - 1] : null;
}

/**
 * The videos of our ad to show: the latest one without text (role final), then the latest with
 * subtitles (role sous-titres), then the latest with background music (role musique), then the
 * latest of any other role.
 * @param {{files?: object[]}} pub
 * @returns {Array<{key: 'final'|'sous-titres'|'musique'|'other', file: object}>}
 */
export function pubVideos(pub) {
  const videos = mediaFiles(pub, 'video');
  const last = (pred) => videos.filter(pred).pop() || null;
  const out = [];
  const plain = last((f) => f.role === 'final');
  const subs = last((f) => f.role === 'sous-titres');
  const music = last((f) => f.role === 'musique');
  const other = last((f) => f.role !== 'final' && f.role !== 'sous-titres' && f.role !== 'musique');
  if (plain) out.push({ key: 'final', file: plain });
  if (subs) out.push({ key: 'sous-titres', file: subs });
  if (music) out.push({ key: 'musique', file: music });
  if (other && !out.length) out.push({ key: 'other', file: other });
  return out;
}

/**
 * The capture of a competitor's scene (role « cle »), else its first image; null without image.
 * @param {{files?: object[]}} scene
 * @returns {object|null}
 */
export function captureOf(scene) {
  const images = mediaFiles(scene, 'image');
  return images.find((f) => f.role === 'cle') || images[0] || null;
}

/**
 * Why an element waits or failed: its control (data.review) when it was refused or is in error,
 * else the last meaningful note of its history; '' when there is none.
 * @param {{status?: string, data?: object, history?: object[]}} item
 * @returns {string}
 */
export function waitingReason(item) {
  const review = typeof item?.data?.review === 'string' ? item.data.review.trim() : '';
  if (review && (item.status === 'review' || item.status === 'error')) return review;
  const notes = (item?.history || []).filter((e) => typeof e.note === 'string' && e.note.trim() && !NOISE_RE.test(e.note.trim()));
  return notes.length ? notes[notes.length - 1].note.trim() : '';
}

/**
 * The making of an ad in numbers: scenes with their image (described or later), images and
 * sequences approved, out of how many.
 * @param {{scenes: Array<{scene: object, images: object[], sequences: object[]}>, extra?: object[]}} d
 * @returns {{scenes: {done: number, total: number}, images: {done: number, total: number}, sequences: {done: number, total: number}}}
 */
export function pubProgress(d) {
  const scenes = d?.scenes || [];
  const images = scenes.flatMap((s) => s.images).filter((x) => x.status !== 'rejected');
  const sequences = [...scenes.flatMap((s) => s.sequences.filter((x) => goesInPub(d?.pub, s.scene, x))), ...(d?.extra || []).filter((x) => usedInPub(d?.pub, x))];
  const done = (list) => list.filter((x) => x.status === 'approved').length;
  return {
    scenes: { done: scenes.filter((s) => s.scene.status === 'ready' || s.scene.status === 'planned').length, total: scenes.length },
    images: { done: done(images), total: images.length },
    sequences: { done: done(sequences), total: sequences.length },
  };
}

/**
 * What keeps the ad from being made: the origin, scenes, images and sequences waiting for a
 * decision (review; a brief whose script waits for validation) or in error, with the reason, in
 * page order.
 * @param {{origin?: object|null, scenes: Array<{scene: object, images: object[], sequences: object[]}>, extra?: object[]}} d
 * @returns {Array<{item: object, reason: string}>}
 */
export function blockers(d) {
  const all = [
    ...(d?.origin ? [d.origin] : []),
    ...(d?.scenes || []).flatMap((s) => [s.scene, ...s.images, ...s.sequences.filter((x) => goesInPub(d?.pub, s.scene, x))]),
    ...(d?.extra || []).filter((x) => usedInPub(d?.pub, x)),
  ];
  const waits = (x) => x.status === 'review' || x.status === 'error' || (x.kind === 'brief' && x.status === 'validation');
  return all.filter(waits).map((item) => ({ item, reason: waitingReason(item) }));
}

/** Statuses of a brief whose script is done (approved, then launched): the ad is in production. */
const BRIEF_DONE = new Set(['approved', 'ready']);

/**
 * Where the script of an ad on brief stands while it is not approved yet: the brief's status
 * (new, writing, validation, rework, error…), else null (approved, ready or any final status; not
 * a brief). The page and the list say it instead of « En production ».
 * @param {{kind?: string, status?: string}|null} origin the pub's origin (dossier) or `origin` of its summary
 * @param {{statuses?: Array<{key: string, final?: boolean}>}|null} [type] the brief's type
 * @returns {string|null}
 */
export function briefPhase(origin, type = null) {
  if (!origin || origin.kind !== 'brief' || typeof origin.status !== 'string' || !origin.status) return null;
  if (BRIEF_DONE.has(origin.status)) return null;
  if ((type?.statuses || []).some((s) => s.key === origin.status && s.final)) return null;
  return origin.status;
}

/**
 * What the owner may decide on a brief: in « validation », approve the script (when the type has
 * an « approved » status) and ask for rework (« rework »).
 * @param {{statuses?: Array<{key: string}>}|null} type the brief's type
 * @param {string} status the brief's status
 * @returns {Array<'approved'|'rework'>}
 */
export function briefActions(type, status) {
  if (status !== 'validation') return [];
  const keys = new Set((type?.statuses || []).map((s) => s.key));
  return ['approved', 'rework'].filter((k) => keys.has(k));
}

/**
 * May the owner change a brief's fields from the page? Not while the concepteur writes it (status
 * « writing », or taken by a task): the server lets a person overwrite a locked element.
 * @param {{status?: string, lock?: object|null}|null} brief
 * @returns {boolean}
 */
export function briefEditable(brief) {
  return Boolean(brief) && brief.status !== 'writing' && !brief.lock;
}

/**
 * The fields of an element for a description, in the type's order then the others: label,
 * text, and whether it is a link, JSON or a long text. Empty values are left out.
 * @param {{data?: object, tags?: string[]}} item
 * @param {{fields?: Array<{key: string, label: string, type: string}>}|null} type
 * @returns {Array<{key: string, label: string, text: string, url?: string, json?: boolean, long?: boolean}>}
 */
export function describeFields(item, type) {
  const data = item?.data || {};
  const defs = type?.fields || [];
  const keys = [...defs.map((f) => f.key), ...Object.keys(data).filter((k) => !defs.some((f) => f.key === k))];
  const out = [];
  for (const key of keys) {
    if (HIDDEN_FIELDS.has(key) || !Object.hasOwn(data, key)) continue;
    const v = data[key];
    if (v === null || v === undefined || v === '') continue;
    const def = defs.find((f) => f.key === key) || { key, label: key, type: 'text' };
    if (def.type === 'json' || (v && typeof v === 'object')) {
      out.push({ key, label: def.label, text: JSON.stringify(v, null, 2), json: true });
    } else if (def.type === 'url' && typeof v === 'string') {
      out.push({ key, label: def.label, text: v, url: v });
    } else {
      const text = String(v);
      out.push({ key, label: def.label, text, long: def.type === 'longtext' || text.length > LONG_CHARS || text.includes('\n') });
    }
  }
  return out;
}

/**
 * The boxes of « Nos séquences », in page order: one per sequence with its scene and its start
 * image (the image it was made from, else the scene's latest image); a scene without sequence
 * yet gets one box with its image and no sequence; the pub's other sequences come last.
 * @param {{scenes: Array<{scene: object, images: object[], sequences: object[]}>, extra?: object[]}} d
 * @returns {Array<{scene: object|null, image: object|null, sequence: object|null}>}
 */
export function sequenceBoxes(d) {
  const out = [];
  for (const { scene, images, sequences } of d?.scenes || []) {
    const fallback = images[images.length - 1] || null;
    if (!sequences.length) {
      out.push({ scene, image: fallback, sequence: null });
      continue;
    }
    for (const sequence of sequences) {
      const parents = new Set(sequence.parents?.map((p) => (typeof p === 'string' ? p : p.id)) || []);
      out.push({ scene, image: images.find((im) => parents.has(im.id)) || fallback, sequence });
    }
  }
  for (const sequence of d?.extra || []) out.push({ scene: null, image: null, sequence });
  return out;
}

/**
 * The sequences an assembled ad is made of (its parents of kind sequence), or null while it is
 * not assembled yet (« En production »: every sequence of its scenes is meant for it).
 * @param {{parents?: Array<string|{id: string, kind?: string}>}|null} pub
 * @returns {Set<string>|null}
 */
export function pubSequenceIds(pub) {
  const ids = (pub?.parents || []).filter((p) => typeof p === 'object' && p?.kind === 'sequence').map((p) => p.id);
  return ids.length ? new Set(ids) : null;
}

/**
 * Whether a sequence goes into the ad: never when refused (status rejected); in an assembled ad,
 * only when it is one of its parents (the owner can take it out, or put it back).
 * @param {object|null} pub
 * @param {{id: string, status?: string}} sequence
 * @returns {boolean}
 */
export function usedInPub(pub, sequence) {
  if (!sequence || sequence.status === 'rejected') return false;
  const ids = pubSequenceIds(pub);
  return !ids || ids.has(sequence.id);
}

/**
 * Was this scene's making forced by a person (« Forcer la création »)? Its sequences go into the
 * ad once validated (the « contrôle de production » adds them, then drops the tag).
 * @param {{tags?: string[]}|null} scene
 * @returns {boolean}
 */
export function isForced(scene) {
  return Boolean(scene?.tags?.includes(FORCED_TAG));
}

/**
 * Whether a sequence of a scene goes into the ad: usedInPub, or a sequence of a forced scene on
 * its way into an assembled ad (not refused).
 * @param {object|null} pub
 * @param {object|null} scene
 * @param {{id: string, status?: string}} sequence
 * @returns {boolean}
 */
export function goesInPub(pub, scene, sequence) {
  if (usedInPub(pub, sequence)) return true;
  return Boolean(sequence) && sequence.status !== 'rejected' && isForced(scene);
}

/**
 * Where a scene without any sequence stands, for its box in « Nos séquences »:
 * - `forced`: a person forced its making, the sequences are being written;
 * - `error`: it failed;
 * - `skipped`: done (its type's final status) without any sequence — left out by the agent who
 *   writes them, with why (`reason`, the last note of its history) and who (`agentId`);
 * - `pending`: its sequences are not written yet.
 * Null when the scene has sequences.
 * @param {{scene: object, sequences: object[]}} entry a scene of the dossier
 * @param {{final?: boolean, error?: boolean}|null} info the status record of the scene
 * @returns {{state: 'forced'|'error'|'skipped'|'pending', reason: string, agentId: string|null}|null}
 */
export function sceneGap(entry, info) {
  if (!entry?.scene || entry.sequences?.length) return null;
  const scene = entry.scene;
  const moves = (scene.history || []).filter((e) => e.action === 'status' || e.action === 'force');
  const by = moves.length ? moves[moves.length - 1].by : null;
  const agentId = typeof by?.agentId === 'string' ? by.agentId : null;
  const reason = waitingReason(scene);
  if (isForced(scene) && !info?.error) return { state: 'forced', reason, agentId };
  if (info?.error) return { state: 'error', reason, agentId };
  if (info?.final) return { state: 'skipped', reason, agentId };
  return { state: 'pending', reason: '', agentId: null };
}

/**
 * The scenes an ad goes without (pure): left out (`skipped`) or failed (`error`), in page order,
 * and the ones whose making was forced and is under way (`forced`).
 * @param {{scenes?: Array<{scene: object, sequences: object[]}>}} d
 * @param {(scene: object) => ({final?: boolean, error?: boolean}|null)} infoOf
 * @returns {{missing: Array<{scene: object, state: string, reason: string, agentId: string|null}>, forced: object[]}}
 */
export function sceneGaps(d, infoOf) {
  const missing = [];
  const forced = [];
  for (const entry of d?.scenes || []) {
    const gap = sceneGap(entry, infoOf(entry.scene));
    if (!gap) {
      if (isForced(entry.scene)) forced.push(entry.scene);
      continue;
    }
    if (gap.state === 'forced') forced.push(entry.scene);
    else if (gap.state === 'skipped' || gap.state === 'error') missing.push({ scene: entry.scene, ...gap });
  }
  return { missing, forced };
}
